import {alpha} from '@mui/material/styles';

// ----------------------------------------------------------------------

import chroma from 'chroma-js';

export function hexToRgb(color) {
  return chroma(color).rgb().join(', ');
}

export function rgba(color, opacity) {
  return `rgba(${hexToRgb(color)}, ${opacity})`;
}


// SETUP COLORS

export const grey = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
};

export const neutral = {
  lighter: '#D0ECFE',
  light: '#95a9bb',
  main: '#3d4e64',
  dark: '#0d295b',
  darker: '#010e34',
  contrastText: '#FFFFFF',
  transparent: {
    main: 'transparent',
  }
};

export const primary = {
  lighter: '#D0ECFE',
  light: '#73BAFB',
  main: '#1877F2',
  dark: '#0C44AE',
  darker: '#042174',
  contrastText: '#FFFFFF',
};

export const secondary = {
  lighter: '#EFD6FF',
  light: '#C684FF',
  main: '#8E33FF',
  dark: '#5119B7',
  darker: '#27097A',
  contrastText: '#FFFFFF',
};

export const info = {
  lighter: '#CAFDF5',
  light: '#61F3F3',
  main: '#00B8D9',
  dark: '#006C9C',
  darker: '#003768',
  contrastText: '#FFFFFF',
};

export const success = {
  lighter: '#C8FAD6',
  light: '#5BE49B',
  main: '#00A76F',
  dark: '#007867',
  darker: '#004B50',
  contrastText: '#FFFFFF',
};

export const warning = {
  lighter: '#FFF5CC',
  light: '#FFD666',
  main: '#FFAB00',
  dark: '#B76E00',
  darker: '#7A4100',
  contrastText: grey[800],
};

export const error = {
  lighter: '#FFE9D5',
  light: '#FFAC82',
  main: '#FF5630',
  dark: '#B71D18',
  darker: '#7A0916',
  contrastText: '#FFFFFF',
};

export const common = {
  black: '#000000',
  white: '#FFFFFF',
};

export const white = {
  main: '#ffffff',
  dark: '#e5e5e5',
};

export const dark = {
  light: '#4e668c',
  main: '#344767',
  focus: '#2c3c58',
  dark: '#1d2e44',
};

export const action = {
  lighter: alpha(grey[200], 0.10),
  hover: alpha(grey[500], 0.08),
  selected: alpha(grey[500], 0.16),
  disabled: alpha(grey[500], 0.8),
  disabledBackground: alpha(grey[500], 0.24),
  focus: alpha(grey[500], 0.24),
  hoverOpacity: 0.08,
  disabledOpacity: 0.48,
};

const base = {
  neutral,
  dark,
  primary,
  secondary,
  info,
  success,
  warning,
  error,
  grey,
  common,
  white,
  divider: alpha(grey[500], 0.2),
  action,
};

const badgeColor = {
  primary: {
    background: '#f8b3ca',
    text: '#cc084b',
  },

  secondary: {
    background: '#d7d9e1',
    text: '#6c757d',
  },

  info: {
    background: '#aecef7',
    text: '#095bc6',
  },

  success: {
    background: '#bce2be',
    text: '#339537',
  },

  warning: {
    background: '#ffd59f',
    text: '#c87000',
  },

  error: {
    background: '#fcd3d0',
    text: '#f61200',
  },

  light: {
    background: '#ffffff',
    text: '#c7d3de',
  },

  dark: {
    background: '#8097bf',
    text: '#1e2e4a',
  },
};

const gradients = {
  primary: {
    main: '#EC407A',
    state: '#D81B60',
  },

  secondary: {
    main: '#747b8a',
    state: '#495361',
  },

  info: {
    main: '#49a3f1',
    state: '#1A73E8',
  },

  success: {
    main: '#66BB6A',
    state: '#43A047',
  },

  warning: {
    main: '#FFA726',
    state: '#FB8C00',
  },

  error: {
    main: '#EF5350',
    state: '#E53935',
  },

  light: {
    main: '#EBEFF4',
    state: '#CED4DA',
  },

  dark: {
    main: '#42424a',
    state: '#191919',
  },
};

const coloredShadows = {
  primary: '#e91e62',
  secondary: '#110e0e',
  info: '#00bbd4',
  success: '#4caf4f',
  warning: '#ff9900',
  error: '#f44336',
  light: '#adb5bd',
  dark: '#404040',
};

// ----------------------------------------------------------------------

export function palette() {
  return {
    ...base,
    mode: 'light',
    text: {
      primary: grey[800],
      secondary: grey[500],
      disabled: grey[400],
      white: white.main,
      neutral: neutral.lighter
    },
    background: {
      paper: '#FFFFFF',
      default: grey[100],
      neutral: grey[200],
    },
    action: {
      ...base.action,
      active: grey[600],
    },
    gradients,
    badgeColor,
    coloredShadows
  };
}
